import React, { useRef } from "react";
import cn from "./cn";

/**
 * OtpInput — the split one-time-code field: N single-character boxes that
 * behave as one control.
 *
 * It exists because there were two independent copies of it — the 2FA modal and
 * institute registration — and they had already drifted: different box sizes,
 * different radii, and a hand-written focus ring that was a third copy of the
 * one `tw-shadow-focus` names. A code field is a control, so it is a control.
 *
 * The value is a STRING, not an array of characters. Both call sites joined and
 * split at every boundary — submitting, validating, clearing — so the array was
 * an internal detail leaking into two pages.
 *
 * ── Behaviour a caller gets for free ────────────────────────────────────────
 * Type to advance · Backspace on an empty box steps back · arrow keys move ·
 * pasting a code anywhere in the row fills the whole row · non-digits are
 * refused rather than silently swallowed.
 *
 * ⚠️ The boxes are `flex-1` with a max width rather than a fixed one. Six 48px
 * boxes plus gaps are 328px, which does not fit the form card on a 360px phone;
 * fixed-width boxes overflowed it. They share the row and cap at their ideal
 * size instead, so the control fits any card without a breakpoint per caller.
 *
 * ⚠️ `tw-border-solid` is load-bearing — Preflight is off, so a bare `tw-border-2`
 * is a width on the UA's `inset` style and the box renders bevelled. Same note as
 * FormControls, which is where it was first caught.
 *
 * Props:
 *   value       — the code so far
 *   onChange    — called with the new code string
 *   onComplete  — called with the code when the last box is filled
 *   length      — number of boxes (default 6)
 *   invalid     — danger tone plus the "that was wrong" shake
 *   disabled
 *   autoFocus   — focus the first box on mount
 *   label       — accessible name for the group (default "Verification code")
 */

// 2px, not the 1px of every other control: an OTP box is a large, isolated
// target with no label beside it, and both implementations this replaces had
// independently chosen the heavier border.
const OTP_BOX_CLASSES =
  "tw-h-12 tw-w-full tw-min-w-0 tw-max-w-[3rem] tw-flex-1 tw-rounded-md tw-border-2 " +
  "tw-border-solid tw-bg-input tw-px-0 tw-text-center tw-text-lg tw-font-semibold " +
  "tw-text-input-fg tw-outline-none tw-transition-[border-color,box-shadow] " +
  "tw-duration-150 disabled:tw-cursor-not-allowed disabled:tw-bg-surface-alt";

// One border-colour class per state, never layered: two equal-specificity colour
// utilities on one element are decided by emitted source order, not by which was
// passed last.
const OTP_VALID_CLASSES = "tw-border-input-line focus:tw-border-primary focus:tw-shadow-focus";
const OTP_INVALID_CLASSES =
  "tw-border-danger tw-animate-shake focus:tw-border-danger focus:tw-shadow-focus-danger";

const OtpInput = ({
  value = "",
  onChange,
  onComplete,
  length = 6,
  invalid = false,
  disabled = false,
  autoFocus = false,
  label = "Verification code",
  className = "",
}) => {
  const inputRefs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || "");

  const emit = (next) => {
    onChange?.(next);
    if (next.length === length && !next.includes(" ")) onComplete?.(next);
  };

  // Write one character at `index`, padding with spaces so a code typed out of
  // order keeps each digit in the box it was typed into.
  const setCharAt = (index, char) => {
    const chars = Array.from({ length }, (_, i) => value[i] || " ");
    chars[index] = char || " ";
    return chars.join("").replace(/\s+$/, "");
  };

  const handleChange = (index, raw) => {
    if (raw && !/^\d+$/.test(raw)) return; // refuse non-digits outright
    const char = raw.slice(-1);
    emit(setCharAt(index, char));
    if (char && index < length - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Pasting is how most people enter a code they were just sent, and it lands on
  // whichever box happened to have focus — so the handler is on the row and
  // always fills from the start.
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    emit(pasted);
    inputRefs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div
      role="group"
      aria-label={label}
      className={cn("tw-flex tw-items-center tw-gap-2", className)}
      onPaste={handlePaste}
    >
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          // On the first box only: iOS and Android offer the SMS code here, and
          // repeating it on every box makes the OS offer to fill each one.
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit.trim()}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={`Digit ${i + 1} of ${length}`}
          aria-invalid={invalid || undefined}
          className={cn(OTP_BOX_CLASSES, invalid ? OTP_INVALID_CLASSES : OTP_VALID_CLASSES)}
        />
      ))}
    </div>
  );
};

export default OtpInput;
